import React from "react";
import PropTypes from "prop-types";
import "./Loading.css";

/**
 * Loading component with multiple variants for different contexts
 * Provides visual feedback during data fetching and processing
 */
const Loading = ({
  variant = "default",
  size = "medium",
  message = "Loading...",
  fullscreen = false,
  overlay = false,
}) => {
  const getLoadingClass = () => {
    const baseClass = "loading-component";
    const classes = [baseClass];

    if (fullscreen) classes.push("loading-fullscreen");
    if (overlay) classes.push("loading-overlay");
    classes.push(`loading-${variant}`);
    classes.push(`loading-${size}`);

    return classes.join(" ");
  };

  const renderSpinner = () => {
    switch (variant) {
      case "dots":
        return (
          <div className="loading-dots">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        );

      case "pulse":
        return <div className="loading-pulse"></div>;

      case "bars":
        return (
          <div className="loading-bars">
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>
        );

      case "circle":
        return <div className="loading-circle"></div>;

      case "iscri":
        return (
          <div className="loading-iscri">
            <div className="iscri-logo">ISCRI</div>
            <div className="iscri-waves">
              <div className="wave"></div>
              <div className="wave"></div>
              <div className="wave"></div>
            </div>
          </div>
        );

      default:
        return <div className="loading-spinner"></div>;
    }
  };

  return (
    <div className={getLoadingClass()}>
      <div className="loading-content">
        {renderSpinner()}
        {message && <div className="loading-message">{message}</div>}
      </div>
    </div>
  );
};

Loading.propTypes = {
  /** Loading animation variant */
  variant: PropTypes.oneOf([
    "default",
    "dots",
    "pulse",
    "bars",
    "circle",
    "iscri",
  ]),
  /** Size of the loading animation */
  size: PropTypes.oneOf(["small", "medium", "large"]),
  message: PropTypes.string,
  fullscreen: PropTypes.bool,
  overlay: PropTypes.bool,
};

/**
 * Pre-configured loading components for specific UI contexts:
 * - PageLoading: fullscreen with ISCRI animation
 * - ChartLoading: mid-size pulse
 * - ButtonLoading: minimal dots
 * - DataLoading: bar animation
 * - OverlayLoading: large circle with overlay
 */
export const PageLoading = ({ message = "Loading page..." }) => (
  <Loading variant="iscri" size="large" message={message} fullscreen overlay />
);

export const ChartLoading = ({ message = "Loading chart data..." }) => (
  <Loading variant="pulse" size="medium" message={message} />
);

export const ButtonLoading = ({ message = "" }) => (
  <Loading variant="dots" size="small" message={message} />
);

export const DataLoading = ({ message = "Fetching data..." }) => (
  <Loading variant="bars" size="medium" message={message} />
);

export const OverlayLoading = ({ message = "Processing..." }) => (
  <Loading variant="circle" size="large" message={message} overlay />
);

/**
 * useLoading - Custom hook to manage loading state logic
 *
 * Returns booleans and helper functions:
 * - startLoading, stopLoading, toggleLoading, setIsLoading
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useLoading = (initialState = false) => {
  const [isLoading, setIsLoading] = React.useState(initialState);

  const startLoading = React.useCallback(() => setIsLoading(true), []);
  const stopLoading = React.useCallback(() => setIsLoading(false), []);
  const toggleLoading = React.useCallback(
    () => setIsLoading((prev) => !prev),
    []
  );

  return {
    isLoading,
    startLoading,
    stopLoading,
    toggleLoading,
    setIsLoading,
  };
};

/**
 * useLoading - Custom hook to manage loading state logic
 *
 * Returns booleans and helper functions:
 * - startLoading, stopLoading, toggleLoading, setIsLoading
 */
// eslint-disable-next-line react-refresh/only-export-components
export const withLoading = (WrappedComponent, loadingProps = {}) => {
  const WithLoadingComponent = (props) => {
    if (props.isLoading) {
      return <Loading {...loadingProps} />;
    }

    return <WrappedComponent {...props} />;
  };

  WithLoadingComponent.displayName = `withLoading(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;

  return WithLoadingComponent;
};

export default Loading;
